import React, { useEffect, useState } from 'react';
import './itemdetail.css';
import ImageSlider from './imageSlider';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import ApiService from '../../../../services/apiService';
import Modifiertable from './modifiertable';

const ItemDetails = ({ Currency }) => {
  const { state } = useLocation();
  const val = state;

  const [category, setCategory] = useState();
  const [subCategory, setSubCategory] = useState();
  const [variants, setVariants] = useState([]);

  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState(null);
  // itemNo -> quantityNew (from GET_MATERIAL_STOCK)
  const [variantStockMap, setVariantStockMap] = useState({});

  // Set variants from itemMasterVarients when val changes
  useEffect(() => {
    if (val) {
      const variantsArray = val?.itemMasterVarients || [];
      setVariants(variantsArray);
    }
    GetCategory();
    GetSubCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val]);

  // Fetch live stock for all variants using GET_MATERIAL_STOCK
  useEffect(() => {
    if (!variants || variants.length === 0) return;

    const fetchVariantStocks = async () => {
      setStockLoading(true);
      setStockError(null);

      try {
        const apiService = new ApiService();
        const token = localStorage.getItem('Token');

        const results = await Promise.all(
          variants
            .filter((v) => v.itemNo) // only call API for variants that have itemNo
            .map(async (v) => {
              const requestBody = {
                request: {
                  endpointUrl: 'https://tp-apg-testza.multichoice.co.za/stock-processing-confirmation-api',
                  templateName: 'MaterialStockByLocationRequest',
                  CountryIdentifier: 'zaf',
                  Plant: '2402',
                  Material: v.itemNo.toString(),
                  token: token,
                },
              };

              const res = await apiService.post('GET_MATERIAL_STOCK', requestBody);

              if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
                const qty = res.data.result?.quantityNew ?? 0;
                return { itemNo: v.itemNo, quantity: qty };
              } else {
                return { itemNo: v.itemNo, quantity: 0 };
              }
            }),
        );

        const map = {};
        results.forEach((r) => {
          map[r.itemNo] = r.quantity;
        });

        setVariantStockMap(map);
      } catch (err) {
        console.error('Error fetching live stock for variants:', err);
        setStockError('Unable to load live stock');
      } finally {
        setStockLoading(false);
      }
    };

    fetchVariantStocks();
  }, [variants]);

  const GetCategory = async () => {
    try {
      if (!val?.category) return;

      const apiService = new ApiService();
      // You can fetch category details if you have an endpoint;
      // for now, we just set it from val
      setCategory({ name: val?.category });
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  const GetSubCategory = async () => {
    try {
      if (!val?.subcategory) return;

      const apiService = new ApiService();
      // You can fetch subcategory details if you have an endpoint;
      // for now, we just set it from val
      setSubCategory({ name: val?.subcategory });
    } catch (error) {
      console.error('Error fetching subcategory data:', error);
    }
  };

  // Total live stock across all variants from API
  const getActualStock = () => {
    if (!variants || variants.length === 0) return 0;

    return variants.reduce((total, variant) => {
      const qty = variantStockMap[variant.itemNo] ?? 0;
      return total + (parseInt(qty, 10) || 0);
    }, 0);
  };

  const getStockStatus = (actualStock) => {
    const stock = parseInt(actualStock, 10) || 0;
    if (stock === 0) return { status: 'Out of Stock', color: '#ef4444' };
    if (stock <= (val?.reorderLevel || val?.minStockLevel || 10)) return { status: 'Low Stock', color: '#f59e0b' };
    return { status: 'In Stock', color: '#10b981' };
  };

  const actualStockQuantity = getActualStock();
  const stockInfo = getStockStatus(actualStockQuantity);

  return (
    <div className="p-5">
      {/* Header */}
      <div className="clean-header-section mb-4">
        <h1 className="item-title">{val?.itemName || 'Item Details'}</h1>
        <div className="item-badges">
          <span className="clean-badge brand">{val?.brand}</span>
          <span className="clean-badge category">{val?.category}</span>
          <span className={`clean-badge status ${val?.itemStatus ? 'active' : 'inactive'}`}>{val?.itemStatus ? 'Active' : 'Inactive'}</span>
        </div>
      </div>

      {/* Row 1: Item Info + Image */}
      <div className="row">
        {/* Item Information */}
        <div className="col-lg-6">
          <div className="clean-card">
            <div className="headtes clean-title">Item Information</div>
            <div className="clean-info-grid">
              <div className="info-row">
                <div className="headtx">Item Name</div>
                <div className="valtx clean-value">{val?.itemName || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Brand</div>
                <div className="valtx clean-value brand-text">{val?.brand || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Category</div>
                <div className="valtx clean-value">{val?.category || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Sub Category</div>
                <div className="valtx clean-value">{val?.subcategory || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Item Description</div>
                <div className="valtx clean-value description">{val?.description || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Supplier</div>
                <div className="valtx clean-value">{val?.supplierName || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              {/* ✅ Warehouse Location (item-level) */}
              <div className="info-row">
                <div className="headtx">Warehouse Location</div>
                <div className="valtx clean-value warehouse-code">{val?.locationWarehouse || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="info-row">
                <div className="headtx">Barcode</div>
                <div className="valtx clean-value barcode">{val?.barcode || 'N/A'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="col-lg-6">
          <div className="clean-card">
            <div className="headtes center clean-title">Item Image</div>
            <div className="orangebx clean-image-container mt-4">
              <ImageSlider val={val} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Price + Inventory */}
      <div className="row mt-5">
        {/* Price Information */}
        <div className="col-lg-6">
          <div className="clean-card">
            <div className="headtes clean-title">Price and Tax Information</div>
            <div className="clean-price-grid">
              <div className="price-row primary">
                <div className="headtx">Retail Price</div>
                <div className="valtx clean-price retail">
                  {Currency.currency.code}{' '}
                  {val?.retailPrice?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || '0.00'}
                </div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="price-row">
                <div className="headtx">Wholesale Price</div>
                <div className="valtx clean-price wholesale">
                  {Currency.currency.code}{' '}
                  {val?.wholesalePrice?.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || '0.00'}
                </div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="price-row">
                <div className="headtx">Tax Rate</div>
                <div className="valtx clean-value tax">{val?.taxRate ?? 0}%</div>
                <div className="separator">
                  <hr />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Inventory Information */}
        <div className="col-lg-6">
          <div className="clean-card">
            <div className="headtes clean-title">Inventory Information</div>
            <div className="clean-inventory-grid">
              {/* Live stock loading/error indicator */}
              {stockLoading && (
                <div className="inventory-row">
                  <div className="headtx">Live Stock</div>
                  <div className="valtx clean-value">
                    <small className="text-muted">Loading latest stock...</small>
                  </div>
                  <div className="separator">
                    <hr />
                  </div>
                </div>
              )}

              {stockError && !stockLoading && (
                <div className="inventory-row">
                  <div className="headtx">Live Stock</div>
                  <div className="valtx clean-value">
                    <small className="text-danger">{stockError}</small>
                  </div>
                  <div className="separator">
                    <hr />
                  </div>
                </div>
              )}

              <div className="inventory-row">
                <div className="headtx">Stock Status</div>
                <div className="valtx clean-value">
                  <span className="stock-status" style={{ color: stockInfo.color }}>
                    {stockInfo.status}
                  </span>
                </div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Current Stock</div>
                <div className="valtx clean-value stock-number">
                  {actualStockQuantity}
                  {variants.length > 1 && <span className="stock-variants-info">(across {variants.length} variants)</span>}
                </div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Min Stock Level</div>
                <div className="valtx clean-value">{val?.minStockLevel ?? val?.minQTY ?? 0}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Max Stock Level</div>
                <div className="valtx clean-value">{val?.maxStockLevel ?? val?.maxQTY ?? 0}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Reorder Level</div>
                <div className="valtx clean-value">{val?.reorderLevel ?? 0}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Inventory UOM</div>
                <div className="valtx clean-value uom">{val?.inventoryUOM || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Packing Type</div>
                <div className="valtx clean-value">{val?.packingType || '—'}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              <div className="inventory-row">
                <div className="headtx">Conversion Rate</div>
                <div className="valtx clean-value">{val?.conversitionRate ?? 1}</div>
                <div className="separator">
                  <hr />
                </div>
              </div>

              {/* Per-variant stocks when multiple variants exist */}
              {variants.length > 1 && (
                <div className="inventory-row">
                  <div className="headtx">Variant Stocks</div>
                  <div className="valtx clean-value">
                    <div className="variant-stocks">
                      {variants.map((variant, index) => (
                        <div key={index} className="variant-stock-item">
                          <span className="variant-name">{variant.itemDescription || `Variant ${index + 1}`}:</span>
                          <span className="variant-stock">
                            {variantStockMap[variant.itemNo] !== undefined ? variantStockMap[variant.itemNo] : stockLoading ? '...' : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="separator">
                    <hr />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Physical Properties */}
      {val?.weight || val?.length || val?.width || val?.height || val?.volume || val?.thickness || val?.shape || val?.size ? (
        <div className="row mt-5">
          <div className="col-lg-12">
            <div className="clean-card">
              <div className="headtes clean-title">Physical Properties</div>
              <div className="row mt-3">
                {val?.weight ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Weight</div>
                      <div className="valtx clean-value">{val.weight}</div>
                    </div>
                  </div>
                ) : null}

                {val?.length ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Length</div>
                      <div className="valtx clean-value">{val.length}</div>
                    </div>
                  </div>
                ) : null}

                {val?.width ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Width</div>
                      <div className="valtx clean-value">{val.width}</div>
                    </div>
                  </div>
                ) : null}

                {val?.height ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Height</div>
                      <div className="valtx clean-value">{val.height}</div>
                    </div>
                  </div>
                ) : null}

                {val?.volume ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Volume</div>
                      <div className="valtx clean-value">{val.volume}</div>
                    </div>
                  </div>
                ) : null}

                {val?.thickness ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Thickness</div>
                      <div className="valtx clean-value">{val.thickness}</div>
                    </div>
                  </div>
                ) : null}

                {val?.shape ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Shape</div>
                      <div className="valtx clean-value">{val.shape}</div>
                    </div>
                  </div>
                ) : null}

                {val?.size ? (
                  <div className="col-md-3 col-6">
                    <div className="phys-item">
                      <div className="headtx">Size</div>
                      <div className="valtx clean-value">{val.size}</div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Row 4: Variants Table */}
      <div className="row mt-5">
        <div className="col-lg-12">
          <div className="clean-card">
            <div className="headtes clean-title">
              Product Variants
              <span className="variant-count">({variants?.length || 0})</span>
            </div>
            <div className="clean-table-wrapper mt-3">
              <div className="table-responsive">
                <table className="table clean-table">
                  <thead>
                    <tr>
                      <th scope="col">Item No</th>
                      <th scope="col">Description</th>
                      <th scope="col">Purchase Price</th>
                      <th scope="col">Sale Price</th>
                      <th scope="col">Stock</th>
                      <th scope="col">VAT</th>
                      {/* ✅ Variant-level warehouse column */}
                      <th scope="col">Warehouse Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants?.length > 0 ? (
                      variants.map((variant, index) => (
                        <tr key={index} className="clean-row">
                          <td className="item-no">{variant?.itemNo || '—'}</td>
                          <td className="description" title={variant?.itemDescription}>
                            {variant?.itemDescription || '—'}
                          </td>
                          <td className="price purchase">
                            {Currency.currency.code}{' '}
                            {variant?.purchasePrice?.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }) || '0.00'}
                          </td>
                          <td className="price sale">
                            {Currency.currency.code}{' '}
                            {variant?.salePrice?.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }) || '0.00'}
                          </td>
                          <td className="stock">
                            <span className="stock-pill">{variant?.stock ?? 0}</span>
                          </td>
                          <td className="vat">{variant?.vat || '—'}</td>
                          {/* ✅ Shows variant.warehouseLocation; falls back to item-level locationWarehouse */}
                          <td className="location" title={variant?.warehouseLocation || val?.locationWarehouse || '—'}>
                            {variant?.warehouseLocation || val?.locationWarehouse || '—'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="empty-variants">
                          <div className="empty-state">
                            <span className="empty-text">No variants available</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Clean Header */
        .clean-header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          padding: 20px 0;
          border-bottom: 2px solid var(--background);
        }

        .item-title {
          color: var(--background);
          font-size: 32px;
          font-weight: 700;
          margin: 0;
        }

        .item-badges {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .clean-badge {
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid;
        }

        .clean-badge.brand {
          background: white;
          color: var(--background);
          border-color: var(--background);
        }

        .clean-badge.category {
          background: white;
          color: #6b7280;
          border-color: #d1d5db;
        }

        .clean-badge.status.active {
          background: white;
          color: #059669;
          border-color: #059669;
        }

        .clean-badge.status.inactive {
          background: white;
          color: #dc2626;
          border-color: #dc2626;
        }

        /* Clean Cards */
        .clean-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 30px;
          height: 100%;
          transition: all 0.2s ease;
        }

        .clean-card:hover {
          border-color: var(--background);
          box-shadow: 0 4px 12px rgba(110, 81, 153, 0.1);
        }

        /* Clean Titles */
        .clean-title {
          background: var(--background);
          margin: -30px -30px 25px -30px;
          padding: 16px 30px;
          border-radius: 12px 12px 0 0;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        /* Clean Values */
        .clean-value {
          transition: color 0.2s ease;
        }

        .clean-value:hover {
          color: var(--background) !important;
        }

        .brand-text {
          color: var(--background) !important;
          font-weight: 600 !important;
        }

        .description {
          line-height: 1.6 !important;
        }

        .barcode {
          font-family: 'Courier New', monospace !important;
          font-weight: 600 !important;
        }

        /* ✅ Warehouse code styling */
        .warehouse-code {
          font-family: 'Courier New', monospace !important;
          font-weight: 700 !important;
          color: var(--background) !important;
          background: rgba(110, 81, 153, 0.08);
          display: inline-block;
          padding: 3px 10px;
          border-radius: 4px;
          letter-spacing: 0.5px;
        }

        /* Clean Prices */
        .clean-price {
          font-weight: 700 !important;
          font-size: 18px !important;
        }

        .clean-price.retail {
          color: var(--background) !important;
        }

        .clean-price.wholesale {
          color: #059669 !important;
        }

        .clean-price.cost {
          color: #dc2626 !important;
        }

        .tax {
          font-weight: 700 !important;
          color: #d97706 !important;
        }

        /* Stock */
        .stock-status {
          font-weight: 600 !important;
          text-transform: uppercase;
          font-size: 14px !important;
        }

        .stock-number {
          font-size: 20px !important;
          font-weight: 700 !important;
          color: var(--background) !important;
        }

        .stock-variants-info {
          font-size: 12px !important;
          font-weight: 400 !important;
          color: #6b7280 !important;
          font-style: italic;
          margin-left: 6px;
        }

        .uom {
          font-weight: 600 !important;
          text-transform: uppercase;
        }

        .variant-stocks {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .variant-stock-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }

        .variant-name {
          color: #6b7280;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .variant-stock {
          font-weight: 600;
          color: var(--background);
        }

        /* Physical Properties */
        .phys-item {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 14px 16px;
          margin-bottom: 16px;
        }

        /* Variant Count Badge */
        .variant-count {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
        }

        /* Clean Table */
        .clean-table-wrapper {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .clean-table {
          margin: 0;
          border: none;
        }

        .clean-table thead {
          background: var(--background);
        }

        .clean-table thead th {
          border: none;
          padding: 16px 12px;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.5px;
        }

        .clean-row {
          transition: background-color 0.2s ease;
          border-bottom: 1px solid #f3f4f6;
        }

        .clean-row:hover {
          background-color: #f9fafb;
        }

        .clean-row:last-child {
          border-bottom: none;
        }

        .clean-table td {
          padding: 14px 12px;
          vertical-align: middle;
          border: none;
        }

        .item-no {
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: var(--background);
        }

        td.description {
          max-width: 250px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .price {
          font-weight: 600;
          text-align: right;
        }

        .price.purchase {
          color: #dc2626;
        }

        .price.sale {
          color: #059669;
        }

        .stock-pill {
          background: var(--background);
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 12px;
        }

        /* ✅ Warehouse location cell in table */
        td.location {
          max-width: 180px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          color: var(--background);
          font-weight: 600;
        }

        /* Empty State */
        .empty-variants {
          text-align: center;
          padding: 40px;
        }

        .empty-state {
          color: #9ca3af;
        }

        .empty-text {
          font-size: 16px;
          font-weight: 500;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .clean-header-section {
            flex-direction: column;
            text-align: center;
          }

          .item-title {
            font-size: 28px;
          }

          .clean-card {
            padding: 20px;
            margin-bottom: 20px;
          }

          .clean-title {
            margin: -20px -20px 20px -20px;
            padding: 14px 20px;
          }

          .clean-table {
            font-size: 12px;
          }

          .clean-table td {
            padding: 10px 8px;
          }
        }

        @media (max-width: 480px) {
          .item-badges {
            justify-content: center;
          }

          .clean-badge {
            font-size: 10px;
            padding: 4px 10px;
          }
        }
      `}</style>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(ItemDetails);
